import type { AvatarCustomization, AvatarItemKey } from "./Customization";
import type { Size } from "./Types";
import * as ColorUtils from "./ColorUtils";

/*
    The purpose of this code is to create a sprite for avatars in a web application.
    The sprite is generated based on user customization data, which includes various avatar features like heads, eyes, mouths, clothes, and accessories.
*/
const DEFAULT_FRAME_SIZE = 64;

export class Sprite {
    private _spriteSheet: HTMLImageElement | null = null;
    public size: Size = {
        width: DEFAULT_FRAME_SIZE,
        height: DEFAULT_FRAME_SIZE,
    };

    private _ready: boolean = false;
    private _onReady: () => void;

    constructor(
        customization: AvatarCustomization,
        width = DEFAULT_FRAME_SIZE,
        height = DEFAULT_FRAME_SIZE,
        onReady = () => {}
    ) {
        this._spriteSheet = null;

        this.size = {
            width,
            height,
        };

        this._onReady = onReady;
        this.generateSheet(customization);
    }

    get isReady() {
        return this._ready;
    }

    get sheet() {
        return this._spriteSheet;
    }

    generateSheet = (customization: AvatarCustomization) => {
        this._ready = false;
        let loadedCount = 0;

        // validate the customization object
        if (
            !customization ||
            !customization.loadOrder ||
            customization.loadOrder.length === 0 ||
            !Array.isArray(customization.loadOrder)
        ) {
            console.error("Invalid customization data provided.");
            return;
        }

        // Initialize an array to hold the loaded images
        const images: HTMLImageElement[] = [];

        // Load all images
        customization.loadOrder?.forEach((loadTarget: AvatarItemKey, idx) => {
            // If the customization does not have the target layer, skip it
            const itemData = customization[loadTarget];
            if (!itemData) {
                // If the image source is not defined, increment the loaded count and skip
                loadedCount++;
                return;
            }

            if (!itemData || !itemData.file || itemData.file.length === 0) {
                // If the item data is not found or it doesn't contain a file location
                // increment the loaded count and skip
                loadedCount++;
                return;
            }

            // Create a new image element
            // and set its source to the file path specified in the customization data
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = `${
                customization?.fileRoot ? customization.fileRoot : ""
            }${itemData.file}`;
            // When the image is loaded, process it based on its color style
            img.onload = async () => {
                images[idx] = await ColorUtils.colorize(
                    img,
                    itemData,
                    customization.skin_color
                );

                // Increment the loaded count
                loadedCount++;

                // If this is the last image to load
                // and all images are fully loaded
                if (loadedCount === customization.loadOrder.length) {
                    // Create the offscreen canvas
                    const canvas = document.createElement("canvas");

                    // Set the canvas dimensions to match the image
                    canvas.style.width = `${img.width}px`;
                    canvas.style.height = `${img.width}px`;
                    canvas.width = img.width;
                    canvas.height = img.height;

                    // Set the canvas to pixelated rendering
                    canvas.style.imageRendering = "pixelated";

                    // Get the 2D context for drawing
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        loadedCount++;
                        return;
                    }

                    // Disable image smoothing for pixel art style
                    ctx.imageSmoothingEnabled = false;

                    // Draw each loaded image onto the canvas
                    // This will stack the images on top of each other
                    // in the order of loadOrder
                    images.forEach((image) => {
                        ctx.drawImage(image, 0, 0, img.width, img.height);
                    });

                    // Image drawn, send it to the sheet.
                    const sheet = new Image();
                    sheet.onload = () => {
                        if (this._spriteSheet !== sheet) return;

                        this._ready = true;
                        this._onReady();
                    };
                    this._spriteSheet = sheet;
                    sheet.src = canvas.toDataURL("image/png");
                }
            };
        });
    };
}
